/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';

import 'App.css';

import { Button } from 'primereact/button';
import FeatherIcon from 'feather-icons-react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { AnomalyRecord } from 'data/entities/AnomalyRecord';
import { DatabaseService } from './stores/DatabaseService';
import { usePowerStationFilterStore } from './stores/PowerStationFilterStore';

import { formatDate, getConditionBgClass, sortDate } from './Utils';
import DeleteModalMessage from './components/DeleteModalMessage';

import "react-datepicker/dist/react-datepicker.css";
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/saga-blue/theme.css';

import { useNavigate } from 'react-router-dom';
import { ConditionType, ConditionTypeLabel } from './model/enumerations/condition-type.model';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const ThermalRecordList = () => {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: 'translation', ...options });
    const [equipments, setEquipments] = useState<Record<number, string>>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<AnomalyRecord | null>(null);
    const thermalRecords = usePowerStationFilterStore((state) => state.thermographicRecords)

    const navigate = useNavigate();
    const dbService = DatabaseService.getInstance();

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!dbService.isInitialized) return;

            const equipments = await dbService.getEquipments();
            const equipmentsRecord = equipments.reduce((acc: Record<number, string>, equipment: any) => {
                acc[equipment.id] = `${equipment.code} - (${equipment.name})`;
                return acc;
            }, {} as Record<number, string>);

            if (!cancelled) setEquipments(equipmentsRecord);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [dbService.isInitialized]);


    const conditionBodyTemplate = (rowData: any) => {
        const condition = rowData?.condition;

        if (condition === ConditionType.NORMAL || !condition) {
            return <div>{ConditionTypeLabel[condition] ?? tt('pages.thermography.inspectionRecordsTable.notAvailable')}</div>;
        }

        const bgClass = getConditionBgClass(condition);
        return <div className={`cell-content ${bgClass}`}>{ConditionTypeLabel[condition]}</div>;
    };


    const equipmentBodyTemplate = (rowData: any) => {
        let text = equipments && equipments[rowData.equipment.id] ? equipments[rowData.equipment.id] : "";
        return <div className={``}>{text}</div>;
    };


    const createdAtBodyTemplate = (rowData: AnomalyRecord) => {
        if (!rowData.createdAt || typeof rowData.createdAt !== 'string') {
            return <div></div>;
        }
        const createdAt = dayjs(rowData.createdAt);
        return <div>{createdAt.isValid() ? formatDate(createdAt.toDate()) : ""}</div>;
    };

    const goToReport = (rowData: AnomalyRecord) => {
        navigate(`/thermalRecordForm/report/${rowData.id}`);
    };


    const actionBodyTemplate = (rowData: AnomalyRecord) => {
        const isSelected = false;

        return (
            <div className={`flex gap-2 action-buttons ${isSelected ? 'selected' : ''}`}>
                <Button
                    onClick={() => goToReport(rowData)}
                    className={`icon-button pdf-button ${isSelected ? "selected" : ""}`}
                    aria-label={tt('pages.thermography.thermalRecordList.actions.viewReportAria')}
                    title={tt('pages.thermography.thermalRecordList.actions.viewPdfTitle')}
                >
                    <FeatherIcon icon="file-text" size={16} />
                    {/* <FontAwesomeIcon icon={faFilePdf}/> */}
                </Button>
                <Button
                    onClick={() => editRecord(rowData)}
                    className={`icon-button edit-button ${isSelected ? 'selected' : ''}`}
                    aria-label={tt('pages.thermography.thermalRecordList.actions.editAria')}
                    title={tt('pages.thermography.thermalRecordList.actions.editTitle')}
                >
                    <FeatherIcon icon="edit-2" size={16} />
                </Button>

                <Button
                    onClick={() => deleteRecord(rowData)}
                    className={`icon-button delete-button ${isSelected ? 'selected' : ''}`}
                    aria-label={tt('pages.thermography.thermalRecordList.actions.deleteAria')}
                    title={tt('pages.thermography.thermalRecordList.actions.removeTitle')}
                >
                    <FeatherIcon icon="trash-2" size={16} />
                </Button>
            </div>
        );
    };

    /***************************** Edit Table Row ************************ */
    const editRecord = (rowData: AnomalyRecord) => {
        navigate(`thermalAnomalyForm/edit/${rowData.id}`);
    };

    /***************************** Delete Table Row ************************ */
    const deleteRecord = (rowData: AnomalyRecord) => {
        setRowToDelete(rowData);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (rowToDelete) {
            dbService.deleteThermographicRecord(rowToDelete.id);
        }
        setShowDeleteModal(false);
        setRowToDelete(null);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setRowToDelete(null);
    };
    /***************************** Delete Table Row ************************ */

    return (
        <div className="card">
            {equipments && (
                <>
                    <DataTable
                        key={`thermal-record-list-${i18n.language}`}
                        value={thermalRecords}
                        className="custom-datatable"
                        size="small"
                        dataKey="id"

                        sortMode="multiple"
                        removableSort

                        scrollable scrollHeight="600px"
                        sortField="createdAt"
                        sortOrder={-1}

                        tableStyle={{ minWidth: '50rem' }}
                        emptyMessage={tt("pages.thermography.inspectionRecordsTable.emptyMessage")}
                    // selection={selectedThermalRecord}
                    // onSelectionChange={(e) => setSelectedThermalRecord(e.value)}
                    >

                        <Column
                            sortable
                            field="name"
                            header={tt('pages.thermography.thermalRecordList.table.record')}
                            style={{ width: '10%' }}
                            body={(rowData) => (
                                <a href={`/thermalAnomalyForm/view/${rowData.id}`} style={{ color: 'blue', textDecoration: 'underline' }}>
                                    {rowData.name}
                                </a>
                            )}
                        />
                        <Column sortable field="equipmentId" body={equipmentBodyTemplate} header={tt('pages.thermography.thermalRecordList.table.equipment')} style={{ width: '20%' }} />
                        <Column sortable field="createdAt" body={createdAtBodyTemplate} header={tt('pages.thermography.thermalRecordList.table.inspectionDate')} sortFunction={sortDate} style={{ width: '7%' }} className="text-center" />
                        <Column sortable field="deltaT" header={tt('pages.thermography.thermalRecordList.table.deltaTemp')} style={{ width: '8%', textAlign: 'center' }} />
                        <Column sortable field="condition" header={tt('pages.thermography.thermalRecordList.table.condition')} body={conditionBodyTemplate} style={{ width: '10%' }} />
                        <Column body={actionBodyTemplate} header={tt('pages.thermography.thermalRecordList.table.actions')} style={{ width: '6%' }} />
                    </DataTable>

                    <DeleteModalMessage
                        message={rowToDelete ? tt('pages.thermography.thermalRecordList.deleteConfirm', { name: rowToDelete.name }) : ''}
                        show={showDeleteModal}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />
                </>
            )}
        </div>
    );
};

export default ThermalRecordList;
